import type { ComponentPropsWithoutRef } from 'react';
import { PreviewCard as BasePreviewCard } from '@base-ui/react/preview-card';
import { merge } from 'lodash-es';
import React from 'react';

import type { GutterProp } from '../../utilities/shared/Gutter';
import type { MaxWidthProps } from '../../utilities/shared/MaxWidth';
import type { MinWidthProps } from '../../utilities/shared/MinWidth';
import type { SpacingScale } from '../../utilities/shared/sizes';
import type { WidthProps } from '../../utilities/shared/Width';
import { baseUIPopupMotionStyles, POPUP_TRANSITION } from '../../common/animations';
import { zIndexes } from '../../common/z_indexes';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { gutterCSS } from '../../utilities/shared/Gutter';
import { maxWidthCSS } from '../../utilities/shared/MaxWidth';
import { minWidthCSS } from '../../utilities/shared/MinWidth';
import { spacingCSS } from '../../utilities/shared/Spacing';
import { widthCSS } from '../../utilities/shared/Width';

export const createPreviewCardHandle = BasePreviewCard.createHandle;

export type PreviewCardHandle<T = unknown> = ReturnType<typeof BasePreviewCard.createHandle<T>>;

// One clock for glide, popup resize, and content slide: the positioner's pointer-events gate only
// spans the whole flight if these stay equal.
const TRANSFER_DURATION = '220ms';
const TRANSFER_EASING = 'cubic-bezier(0.2, 0, 0, 1)';
const TRANSFER_FADE_DURATION = '120ms';
const TRANSFER_SLIDE_DISTANCE = '24px';

const PreviewCardPopover = styled(BasePreviewCard.Popup, baseUIPopupMotionStyles, {
  display: 'flex',
  flexDirection: 'column',
  // Half of Base UI's auto-resize contract; the positioner holds the other half.
  width: 'var(--popup-width, auto)',
  height: 'var(--popup-height, auto)',
  overflow: 'auto',
  background: colors.bgApplicationLight,
  borderRadius: '$10',
  boxShadow: shadows.overlayLight,

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
    boxShadow: shadows.overlayDark,
  },

  '@media (prefers-reduced-motion: no-preference)': {
    transition: `${POPUP_TRANSITION}, width ${TRANSFER_DURATION} ${TRANSFER_EASING}, height ${TRANSFER_DURATION} ${TRANSFER_EASING}`,
  },

  // base-ui (1.8) can lag between marking a popup closed and its exit styles, flashing the stale card.
  '&[data-closed]': {
    opacity: 0,
    transform: 'var(--popup-hidden-transform)',
  },
});
PreviewCardPopover.displayName = 'PreviewCardPopover';

const PreviewCardPositioner = styled(BasePreviewCard.Positioner, {
  zIndex: zIndexes.overlay,

  // The other half of auto-resize: a viewport takes the popup out of flow, and without a held size
  // the positioner collapses to 0×0 and `align: 'center'` lands the card off-centre.
  width: 'var(--positioner-width, auto)',
  height: 'var(--positioner-height, auto)',

  // The glide between triggers sharing a handle. Base UI suppresses transitions while the popup
  // is entering, so a fresh open never glides in from wherever the card last stood.
  '@media (prefers-reduced-motion: no-preference)': {
    transitionProperty: 'top, right, bottom, left, transform',
    transitionDuration: TRANSFER_DURATION,
    transitionTimingFunction: TRANSFER_EASING,
  },

  // In flight the card would occlude the trigger under the pointer, flapping its hover and
  // retargeting the transfer — what got the glide removed the first time.
  '&:has([data-transitioning])': {
    pointerEvents: 'none',
  },
});
PreviewCardPositioner.displayName = 'PreviewCardPositioner';

const PreviewCardPortal = styled(BasePreviewCard.Portal);
PreviewCardPortal.displayName = 'PreviewCardPortal';

export type PreviewCardViewportProps = ComponentPropsWithoutRef<typeof BasePreviewCard.Viewport>;

export const PreviewCardViewport = styled(BasePreviewCard.Viewport, {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,

  '& [data-current], & [data-previous]': {
    boxSizing: 'border-box',
    width: 'var(--popup-width)',
  },

  '&[data-transitioning]': {
    overflow: 'clip',
  },

  '@media (prefers-reduced-motion: no-preference)': {
    '& [data-current], & [data-previous]': {
      opacity: 1,
      translate: '0 0',
      willChange: 'translate, opacity',
      transition: `translate ${TRANSFER_DURATION} ${TRANSFER_EASING}, opacity ${TRANSFER_FADE_DURATION} ${TRANSFER_EASING}`,
    },

    '& [data-current][data-starting-style], & [data-previous][data-ending-style]': {
      opacity: 0,
    },

    '&[data-activation-direction~="right"] [data-current][data-starting-style], &[data-activation-direction~="left"] [data-previous][data-ending-style]':
      {
        translate: `${TRANSFER_SLIDE_DISTANCE} 0`,
      },
    '&[data-activation-direction~="left"] [data-current][data-starting-style], &[data-activation-direction~="right"] [data-previous][data-ending-style]':
      {
        translate: `-${TRANSFER_SLIDE_DISTANCE} 0`,
      },
  },
});
PreviewCardViewport.displayName = 'PreviewCardViewport';

export type PreviewCardContentProps = {
  /**
   * How to align the popup relative to the trigger. Defaults to `'center'`.
   */
  alignment?: 'start' | 'center' | 'end';
  /**
   * The content to display inside the preview card popup.
   */
  children: React.ReactNode;
  /**
   * The amount of padding to add to the popup to prevent it from being cut off by the viewport. Defaults to `12`.
   */
  collisionPadding?: SpacingScale;
  /**
   * The amount of spacing to add between the content and the popup. Defaults to `12`.
   */
  gap?: SpacingScale;
  /**
   * Which side of the trigger to display the popup. Defaults to `'bottom'`.
   */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * Distance between the trigger and the popup in pixels. Defaults to `8`.
   */
  sideOffset?: SpacingScale;
  /**
   * When true, wraps children in `PreviewCard.Viewport` so content can animate when switching
   * between multiple triggers that share one `PreviewCard` via `createPreviewCardHandle` and `payload`.
   */
  viewport?: boolean;
} & GutterProp &
  WidthProps &
  MaxWidthProps &
  MinWidthProps &
  GutterProp;

export function PreviewCardContent({
  alignment = 'center',
  children,
  collisionPadding = 12,
  gap = 12,
  gutter = { context: 'content', edge: 'all' },
  side = 'bottom',
  sideOffset = 8,
  viewport = false,
  width,
  maxWidth = 360,
  minWidth = 240,
}: PreviewCardContentProps) {
  const gutterStyles = gutterCSS(gutter);
  const previewCardPopoverCSS = merge(
    {
      maxWidth: 'var(--available-width)',
      maxHeight: 'var(--available-height)',
    },
    spacingCSS(gap),
    viewport ? undefined : gutterStyles,
    widthCSS(width),
    maxWidth ? maxWidthCSS(maxWidth) : undefined,
    minWidthCSS(minWidth),
  );

  const inner = viewport ? (
    <PreviewCardViewport css={{ '& [data-current], & [data-previous]': gutterStyles }}>
      {children}
    </PreviewCardViewport>
  ) : (
    children
  );

  return (
    // Not kept mounted: on base-ui 1.8 a kept-mounted preview card stops reopening after a few
    // open/close rounds.
    <PreviewCardPortal>
      <PreviewCardPositioner
        side={side}
        sideOffset={sideOffset}
        align={alignment}
        collisionPadding={collisionPadding}
      >
        <PreviewCardPopover css={previewCardPopoverCSS}>{inner}</PreviewCardPopover>
      </PreviewCardPositioner>
    </PreviewCardPortal>
  );
}

export type PreviewCardTriggerProps = ComponentPropsWithoutRef<typeof BasePreviewCard.Trigger>;

export const PreviewCardTriggerContainer = styled(BasePreviewCard.Trigger);
PreviewCardTriggerContainer.displayName = 'PreviewCardTriggerContainer';

export function PreviewCardTrigger({
  closeDelay = 600,
  delay = 100,
  ...props
}: PreviewCardTriggerProps) {
  return <PreviewCardTriggerContainer closeDelay={closeDelay} delay={delay} {...props} />;
}

export type PreviewCardProps = ComponentPropsWithoutRef<typeof BasePreviewCard.Root>;

export const PreviewCard = styled(BasePreviewCard.Root);
PreviewCard.displayName = 'PreviewCard';
